<script setup lang="ts">
/**
 * 首页（空闲态）。
 *
 * 触屏机 90% 的时间停在这一屏，所以它要同时干三件事：
 *   1. 招呼顾客：大字告诉顾客「这里可以自己点餐」；
 *   2. 告诉顾客现在能不能真的扫码付款（模拟支付时必须说清楚）；
 *   3. 一键开始，不要有任何多余步骤 —— 顾客不排队的时候也不会研究界面。
 */
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import ModeBanner from '../components/ModeBanner.vue';
import { kiosk } from '../stores/kiosk';

const router = useRouter();
const storeName = computed(() => kiosk.state.menu[0]?.name ?? '');
const storeLabel = computed(() => kiosk.state.paymentConfig?.mode === 'wechat' ? '扫码点餐 · 自助付款' : '扫码点餐 · 到收银台付款');

onMounted(() => {
  // 首页也要知道菜单和支付能力（菜单取不到时提示店员，而不是等顾客点进去才发现）
  void kiosk.init();
});

function start(): void {
  kiosk.startSession();
  void router.push('/menu');
}
</script>

<template>
  <div class="welcome">
    <ModeBanner :offline="kiosk.state.offline" />

    <div class="welcome__main">
      <div class="welcome__brand">金骅 · 潮汕牛肉饭</div>
      <div class="welcome__title">欢迎光临</div>
      <div class="welcome__sub">{{ storeLabel }}</div>

      <button class="btn btn--primary btn--xl welcome__start" data-testid="start-order" @click="start">
        开始点餐
      </button>

      <div class="welcome__tips">
        <span>· 点屏幕上的菜就能加入购物车</span>
        <span>· 下单后屏幕上会出现付款二维码</span>
        <span>· 拿好取餐号，到取餐口取餐</span>
      </div>
    </div>

    <div v-if="kiosk.state.menuError" class="banner banner--danger">
      ⚠️ {{ kiosk.state.menuError }}
    </div>
  </div>
</template>

<style scoped>
.welcome {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 22px 26px;
}

.welcome__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
}

.welcome__brand {
  color: var(--brand);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 6px;
}

.welcome__title {
  font-size: 68px;
  font-weight: 900;
  letter-spacing: 8px;
}

.welcome__sub {
  color: var(--muted);
  font-size: 22px;
}

.welcome__start {
  margin-top: 26px;
  min-width: 420px;
  letter-spacing: 6px;
}

.welcome__tips {
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--muted);
  font-size: 17px;
}

/* 竖屏时字号收一收，避免溢出 */
@media (max-aspect-ratio: 1/1) {
  .welcome__title {
    font-size: 46px;
  }

  .welcome__start {
    min-width: 80%;
  }
}

@media (max-width: 860px) {
  .welcome {
    padding: 16px;
    gap: 12px;
  }

  .welcome__brand {
    font-size: 18px;
    letter-spacing: 4px;
  }

  .welcome__title {
    font-size: 44px;
    letter-spacing: 6px;
  }

  .welcome__sub {
    font-size: 17px;
  }

  .welcome__start {
    min-width: 100%;
    margin-top: 18px;
    letter-spacing: 4px;
  }

  .welcome__tips {
    margin-top: 16px;
    font-size: 14px;
    text-align: left;
    align-self: stretch;
  }
}

</style>
